<template>
    <div class="report-details-container">
      <div v-if="isLoading">
        <el-skeleton :loading="isLoading" :rows="10" />
      </div>
      <div v-else>
        <div>
          <span style="font-size: 13px">Report# {{  document.id }}</span>
          <el-row :gutter="20">
            <el-col :span="18"
              ><h2 class="summary">{{  document.summary }}</h2></el-col
            >
            <el-col :span="6" style="text-align: right !important">
              <el-select
                style="width: 150px !important"
                :model-value="status"
                @update:model-value="updateStatus"
                class="mr-3"
              >
                <el-option
                  v-for="item in ['To Do', 'In Progress', 'In Review', 'Done']"
                  :key="item"
                  :label="item"
                  :value="item"
                >
                </el-option>
              </el-select>
            </el-col>
          </el-row>
          <div class="mt-2">
            <el-tag
              class="mr-2"
              v-if="document.appType"
              type="primary"
              >{{ document.appType }}</el-tag
            >
            <el-tag
              v-if="document.category"
              type="info"
              >{{ document.category }}</el-tag
            >
          </div>
        </div>
        <div>
          <div class="mt-4">
            <span class="label">Created By</span>
          </div>
          <div>{{ document.firstName }} {{ document.lastName }}</div>
          <div style="font-size: 13px">{{  document.email }}</div>
        </div>
        <div>
          <div class="mt-3">
            <span class="label">Created At</span>
          </div>
          <div>
            {{ document.createdAt.toDate().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric' }) }}
          </div>
        </div>
        <div>
          <div class="mt-3">
            <span class="label">Description</span>
          </div>
          <div>
            {{ document.description }}
          </div>
        </div>
        <div>
          <div class="mt-3">
            <span class="label">Assignee</span>
          </div>
          <div class="assignment-container mt-1">
            <el-popover placement="bottom-start" :width="300" trigger="click">
              <template #reference>
                <button v-if="document.assignedTo">
                  <span
                    class="avatar mr-0"
                    >{{ getInitials(document.assignedTo) }}</span
                  >
                </button>
                <button v-else class="operations-btn">
                  <i class="ri-user-fill"></i>
                </button>
              </template>
              <template #default>
                <el-select
                  :model-value="assignedTo"
                  @update:model-value="updateAssignment($event)"
                  placeholder="Assign to"
                  style="width: 100% !important"
                >
                  <el-option
                    class="assign-to-select"
                    v-for="item in ['Arvin Tashakori', 'Megan Ma']"
                    :key="item"
                    :label="item"
                    :value="item"
                  >
                    <span class="avatar">{{ getInitials(item) }}</span>
                    <span>{{ item }}</span>
                  </el-option>
                </el-select>
              </template>
            </el-popover>
          </div>
        </div>
        <div>
          <div class="mt-3">
            <span class="label">Attachments</span>
          </div>
          <div class="image-wall">
            <el-image
              v-for="(image, index) in document.screenshots"
              :key="index"
              class="image-item"
              :src="image"
              :initial-index="currentImageIndex"
              :preview-src-list="document.screenshots"
              @click.native="updatePreviewImageIndex(index)"
              :fit="'contain'"
            ></el-image>
          </div>
        </div>

        <div>
          <div class="mt-3">
            <span class="label">Comments</span>
          </div>
          <div>
            <el-timeline class="mt-3">
              <el-timeline-item
                v-for="note in document.comments"
                :key="note.timestamp"
                :timestamp="note.timestamp.toDate().toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })"
              >
                <div class="note-meta">
                  <span class="note-user">{{ note.user }}</span>
                </div>
                {{ note.content }}
              </el-timeline-item>
            </el-timeline>

            <div class="input-with-avatar mt-3">
              <span
                class="avatar mr-2"
                >{{ getInitials(loggedInUser.displayName) }}</span
              >
              <div class="flex-grow">
                <el-input
                  class="flex-grow"
                  @update:model-value="value => newNotesContent = value"
                  :model-value="newNotesContent"
                  type="textarea"
                  placeholder="Add a note"
                ></el-input>

                <el-button
                  style="font-size: 12px"
                  class="mt-3"
                  @click="saveNote"
                  >Save</el-button
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>

<script>
import { onMounted, watch, ref, onBeforeUnmount } from 'vue'
import { doc, onSnapshot } from 'firebase/firestore'
import firebase from '../../../../firebase'
import { updateDocument } from '../../../services/useDocument'
import { Timestamp, arrayUnion } from 'firebase/firestore'

export default {
  name: 'BugReportDetails',
  props: ['id'],
  setup (props, context) {
    const isLoading = ref(true) // Start with loading true to show the skeleton initially
    const document = ref(null)
    const unsub = ref(null) // Ref for keeping track of the unsubscribe function
    const assignedTo = ref(null)
    const status = ref(null)
    const newNotesContent = ref(null)
    const loggedInUser = firebase.firebaseAuth.currentUser
    const currentImageIndex = ref(0)

    watch(() => props.id, (newId, oldId) => {
      if (unsub.value) {
        unsub.value()
      }
      assignedTo.value = null
      status.value = null
      isLoading.value = true
      newNotesContent.value = null
      currentImageIndex.value = 0
      unsub.value = onSnapshot(doc(firebase.db, 'bugReports', newId), (docSnapshot) => {
        if (docSnapshot.exists()) {
          document.value = { id: docSnapshot.id, ...docSnapshot.data() }
          status.value = document.value.status || 'To Do'
        } else {
          document.value = null
        }
        isLoading.value = false
      }, (error) => {
        console.error('Error fetching document:', error)
        isLoading.value = false
      })
    }, { immediate: true })

    const getInitials = (name) => {
      if (!name) return ''
      return name.split(' ').map(word => word[0]).join('').toUpperCase()
    }

    const updateAssignment = async (name) => {
      const { handlelUpdate } = updateDocument('bugReports', props.id)
      assignedTo.value = name
      await handlelUpdate({ assignedTo: name })
      context.emit('reload-reports')
    }

    const updateStatus = async (status) => {
      const { handlelUpdate } = updateDocument('bugReports', props.id)
      await handlelUpdate({ status })
      context.emit('reload-reports')
    }

    onBeforeUnmount(() => {
      if (unsub.value) {
        unsub.value()
      }
    })

    const updatePreviewImageIndex = (index) => {
      currentImageIndex.value = index
    }
    const saveNote = async () => {
      if (newNotesContent.value === null || newNotesContent.value === '') return
      const { handlelUpdate } = updateDocument('bugReports', props.id)
      const noteItem = {
        user: loggedInUser.email,
        content: newNotesContent.value,
        timestamp: Timestamp.now()
      }
      handlelUpdate({ comments: arrayUnion(noteItem) })
    }

    return {
      document,
      isLoading,
      getInitials,
      assignedTo,
      updateAssignment,
      status,
      updateStatus,
      newNotesContent,
      loggedInUser,
      saveNote,
      updatePreviewImageIndex,
      currentImageIndex
    }
  }
}
</script>

  <style scoped>
  .report-details-container {
    min-height: 500px;
  }

  .summary {
    background-color: var(--ds-surface, #fff);
    color: var(--ds-text, #172b4d);
    margin-top: 2px;
    font-size: 18px;
    font-weight: 500;
  }

  .label {
    color: var(--ds-text, #172b4d);
    font-size: 0.8rem;
    font-weight: bold;
  }

  .avatar {
    display: inline-block;
    background-color: var(--accent-color);
    color: white;
    font-weight: bold;
    font-size: 12px;
    width: 28px;
    height: 28px;
    line-height: 28px;
    border-radius: 50%;
    text-align: center;
    margin-right: 8px;
  }

  .operations-btn {
    background-color: var(--dark-grey);
    border: none;
    border-radius: 500px;
    cursor: pointer;
    padding: 2px 7px;
    margin: 0;
    font-size: 14px;
    color: var(--text-color);
  }

  .image-wall {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    margin-top: 10px;
    gap: 8px;
  }

  .image-item {
    width: 100%;
    object-fit: cover;
    cursor: pointer;
    overflow: hidden;
    border: 1px dotted var(--border-color);
    border-radius: 6px;
    box-sizing: border-box;
    width: 120px;
    height: 120px;
    display: inline-flex;
  }

  .flex-grow {
    flex-grow: 1; /* Allows the input to grow to fill remaining space */
  }

  /* If you are using Element Plus and want to adjust the textarea height */
  .el-textarea .el-textarea__inner {
    min-height: 40px; /* Adjust based on your needs */
    height: 40px; /* Adjust based on your needs */
  }

  .input-with-avatar {
    display: flex; /* Establishes a flex container */
    align-items: top; /* Aligns children vertically in the center */
    gap: 10px; /* Adds space between the avatar and the input */
  }

  </style>

  <style>
  .el-image-viewer__btn.el-image-viewer__actions {
    display: none;
  }

  .el-dialog {
    padding: 20px !important;
  }
</style>
